import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'

export interface DesignPreviewUpdate {
  op: 'preview_update'
  image_url: string
  last_rendered_version: number
  is_generating: boolean
  session_uuid: string
  duration: number | null
}

export const useDesignPreviewSync = (eventsData: AlmanacFileCalendarItem[]) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  useEffect(() => {
    if (!auth || !websocketConnection) return

    const designsGenerating = eventsData.filter(
      (event) => event.asset_type === 'Design' && (event.image_url_generating || !event.image_url),
    )

    const subscriptions = designsGenerating.map((event) => {
      return websocketConnection.subscriptions.create(
        {
          channel: 'DesignPreviewChannel',
          key: event.key,
        },
        {
          received(data: DesignPreviewUpdate) {
            if (data.op === 'preview_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, event.key, (f) => ({
                ...f,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || f.duration,
                is_generating: false,
              }))

              updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, event.key, (file) => ({
                ...file,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || file.duration,
                is_generating: false,
              }))
            }
          },
        },
      )
    })

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe())
    }
  }, [auth, websocketConnection, eventsData, queryClient, workspaceId])
}
